// Polyfill for `showOpenFilePicker` API
// See https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/wicg-file-system-access/index.d.ts
// See also https://caniuse.com/?search=showOpenFilePicker
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';

interface OpenFilePickerOptions {
  types?:
    | {
        description?: string | undefined;
        accept: Record<string, string | string[]>;
      }[]
    | undefined;
  excludeAcceptAllOption?: boolean | undefined;
  multiple?: boolean | undefined;
}

declare global {
  interface Window {
    // Window API: showOpenFilePicker
    showOpenFilePicker?: (
      options?: OpenFilePickerOptions
    ) => Promise<FileSystemFileHandle[]>;
  }
}

// See [Common MIME types](https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types)
const FileTypes: NonNullable<OpenFilePickerOptions['types']> = [
  { description: 'PDF', accept: { 'application/pdf': ['.pdf'] } },
  {
    description: 'Images',
    accept: {
      'image/*': [
        '.avif',
        '.gif',
        // '.ico',
        '.jpeg',
        '.jpg',
        '.png',
        '.tif',
        '.tiff',
        // '.svg',
        '.webp',
      ],
    },
  },
  {
    description: 'Videos',
    accept: {
      'video/*': [
        '.avi',
        '.mp4',
        '.mpeg',
        '.ogg',
        // '.ts',
        '.webm',
        '.3gp',
        '.3g2',
      ],
    },
  },
  {
    description: 'Audios',
    accept: {
      'audio/*': [
        '.aac',
        '.mid',
        '.midi',
        '.mp3',
        '.oga',
        '.opus',
        '.wav',
        '.weba',
        '.3gp',
        '.3g2',
      ],
    },
  },
  {
    description: 'Markdown',
    accept: {
      'text/markdown': ['.md', '.markdown'],
    },
  },
  {
    description: 'Html',
    accept: {
      'text/html': ['.html', '.htm'],
    },
  },
  {
    description: 'Zip',
    accept: {
      'application/zip': ['.zip'],
    },
  },
  {
    description: 'Docx',
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
    },
  },
  {
    description: 'MindMap',
    accept: {
      'text/xml': ['.mm', '.opml', '.xml'],
    },
  },
];

// StreamLain Hook: Get additional file types from window global
declare global {
  interface Window {
    __streamlainHooks?: {
      fileTypes?: Array<{
        description: string;
        accept: Record<string, string[]>;
      }>;
      prepareDemoImportFiles?: (input: {
        entry?: string;
        intent?: string;
        workspaceId?: string;
        files: File[];
      }) => Promise<File[]>;
    };
  }
}

/** The host app substitutes owned samples before a semantic importer reads bytes. */
export async function prepareDemoImportFiles(input: {
  entry?: string;
  intent?: string;
  workspaceId?: string;
  files: File[];
}): Promise<File[]> {
  if (typeof window === 'undefined') return input.files;
  const hooks = window.__streamlainHooks;
  if (!hooks) return input.files;
  if (!hooks.prepareDemoImportFiles) {
    throw new Error('Demo import policy is unavailable');
  }
  return hooks.prepareDemoImportFiles(input);
}

function getExtendedFileTypes(): NonNullable<OpenFilePickerOptions['types']> {
  const streamlainTypes = window.__streamlainHooks?.fileTypes ?? [];
  return [...FileTypes, ...streamlainTypes];
}

/**
 * See https://web.dev/patterns/files/open-one-or-multiple-files/
 * Note: StreamLain may extend this via __streamlainHooks.fileTypes
 */
type AcceptTypes =
  | 'Any'
  | 'PDF'
  | 'Images'
  | 'Videos'
  | 'Audios'
  | 'Markdown'
  | 'Html'
  | 'Zip'
  | 'Docx'
  | 'MindMap'
  // StreamLain extensions (registered via hooks)
  | 'Csv'
  | 'Excel';

export async function openFilesWith(
  acceptType: AcceptTypes = 'Any',
  multiple: boolean = true,
  options?: { prepareDemo?: boolean; workspaceId?: string }
): Promise<File[] | null> {
  // Feature detection. The API needs to be supported
  // and the app not run in an iframe.
  const supportsFileSystemAccess =
    'showOpenFilePicker' in window &&
    (() => {
      try {
        return window.self === window.top;
      } catch {
        return false;
      }
    })();

  // If the File System Access API is supported…
  let files: File[] | null;
  if (supportsFileSystemAccess && window.showOpenFilePicker) {
    try {
      // Use extended file types (includes StreamLain hooks)
      const extendedTypes = getExtendedFileTypes();
      const fileType = extendedTypes.find(i => i.description === acceptType);
      if (acceptType !== 'Any' && !fileType)
        throw new BlockSuiteError(
          ErrorCode.DefaultRuntimeError,
          `Unexpected acceptType "${acceptType}"`
        );
      const pickerOpts = {
        types: fileType ? [fileType] : undefined,
        excludeAcceptAllOption: acceptType === 'PDF',
        multiple,
      } satisfies OpenFilePickerOptions;
      // Show the file picker, optionally allowing multiple files.
      const handles = await window.showOpenFilePicker(pickerOpts);

      files = await Promise.all(handles.map(handle => handle.getFile()));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return null;
      throw err;
    }
  } else {
    // Fallback if the File System Access API is not supported.
    files = await new Promise<File[] | null>(resolve => {
      // Append a new `<input type="file" multiple? />` and hide it.
      const input = document.createElement('input');
      input.classList.add('affine-upload-input');
      input.style.display = 'none';
      input.type = 'file';
      input.multiple = multiple;

      if (acceptType !== 'Any') {
        // For example, `accept="image/*"` or `accept="video/*,audio/*"`.
        // Use extended file types (includes StreamLain hooks)
        const extendedTypes = getExtendedFileTypes();
        input.accept = Object.keys(
          extendedTypes.find(i => i.description === acceptType)?.accept ?? ''
        ).join(',');
      }
      document.body.append(input);
      // The `change` event fires when the user interacts with the dialog.
      input.addEventListener('change', () => {
        // Remove the `<input type="file" multiple? />` again from the DOM.
        input.remove();

        resolve(input.files ? Array.from(input.files) : null);
      });
      // The `cancel` event fires when the user cancels the dialog.
      input.addEventListener('cancel', () => resolve(null));
      // Show the picker.
      if ('showPicker' in HTMLInputElement.prototype) {
        input.showPicker();
      } else {
        input.click();
      }
    });
  }
  if (!files || options?.prepareDemo === false) return files;
  return prepareDemoImportFiles({ files, workspaceId: options?.workspaceId });
}

export async function openSingleFileWith(
  acceptType?: AcceptTypes,
  options?: { prepareDemo?: boolean; workspaceId?: string }
): Promise<File | null> {
  const files = await openFilesWith(acceptType, false, options);
  return files?.at(0) ?? null;
}

export async function getImageFilesFromLocal(workspaceId?: string) {
  const files = await openFilesWith('Images', true, { workspaceId });
  return files ?? [];
}

export function downloadBlob(blob: Blob, name: string) {
  const dataURL = URL.createObjectURL(blob);
  const tmpLink = document.createElement('a');
  const event = new MouseEvent('click');
  tmpLink.download = name;
  tmpLink.href = dataURL;
  tmpLink.dispatchEvent(event);

  tmpLink.remove();
  URL.revokeObjectURL(dataURL);
}

// Use lru strategy is a better choice, but it's just a temporary solution.
const MAX_TEMP_DATA_SIZE = 100;
const tempImageMap = new Map<
  string,
  {
    // This information comes from pictures.
    // If the user switches between pictures and attachments,
    // this information should be retained.
    width: number | undefined;
    height: number | undefined;
  }
>();

/**
 * Because the image block and attachment block have different props.
 * We need to save some data temporarily when converting between them to ensure no data is lost.
 *
 * For example, before converting from an image block to an attachment block,
 * we need to save the image's width and height.
 *
 * See also https://github.com/toeverything/blocksuite/pull/4583#pullrequestreview-1610662677
 *
 * @internal
 */
export function withTempBlobData() {
  const saveImageData = (
    sourceId: string,
    data: { width: number | undefined; height: number | undefined }
  ) => {
    if (tempImageMap.size > MAX_TEMP_DATA_SIZE) {
      console.warn(
        'Clear temp image data. It may cause image width and height loss when converting between image and attachment.'
      );
      tempImageMap.clear();
    }

    tempImageMap.set(sourceId, data);
  };
  const getImageData = (blockId: string) => {
    const data = tempImageMap.get(blockId);
    tempImageMap.delete(blockId);
    return data;
  };
  return {
    saveImageData,
    getImageData,
  };
}
